import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { physioAPI, patientAPI } from '../../utils/api';

const SOURCES = ['OPD', 'IPD', 'A&E', 'Self'];
const GOALS = ['Pain Relief', 'Mobility Improvement', 'Post-Op Rehab', 'Neurological Rehab', 'Strength Training', 'Gait Training', 'Respiratory Physio', 'Other'];

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default function PhysioWorklist() {
    const navigate = useNavigate();
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [patientSearch, setPatientSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [form, setForm] = useState({ source: 'OPD', diagnosis: '', treatment_goals: '', sessions_prescribed: 10, notes: '' });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const res = await physioAPI.getWorklist();
            setReferrals(res.data.referrals);
        } catch { toast.error('Failed to load worklist'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const searchPatient = async () => {
        if (!patientSearch.trim()) return;
        try {
            const res = await patientAPI.search(patientSearch);
            setSearchResults(res.data.patients);
            if (!res.data.patients.length) toast.warning('No patient found');
        } catch { toast.error('Search failed'); }
    };

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleCreate = async () => {
        if (!selectedPatient) return toast.error('Select a patient first');
        if (!form.diagnosis.trim()) return toast.error('Enter diagnosis');
        setSaving(true);
        try {
            await physioAPI.createReferral({ patient_id: selectedPatient.id, ...form });
            toast.success('Referral created');
            setShowForm(false);
            setSelectedPatient(null);
            setPatientSearch('');
            setSearchResults([]);
            setForm({ source: 'OPD', diagnosis: '', treatment_goals: '', sessions_prescribed: 10, notes: '' });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setSaving(false); }
    };

    const progressColor = (done, total) => {
        const pct = total ? (done / total) * 100 : 0;
        if (pct >= 100) return '#16a34a';
        if (pct >= 50) return '#d97706';
        return '#dc2626';
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🦽</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>Physiotherapy</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Rehab & Physical Therapy Worklist</p>
                    </div>
                </div>
                <button onClick={() => setShowForm(!showForm)}
                    style={{ padding: '9px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    + New Referral
                </button>
            </div>

            {/* New Referral Form */}
            {showForm && (
                <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 20, border: '1px solid #bfdbfe' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>New Physiotherapy Referral</h3>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 5 }}>Patient Search *</label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                                placeholder="Search by Name / MRN / CNIC..."
                                style={{ ...inputStyle, flex: 1 }}
                                onKeyDown={e => e.key === 'Enter' && searchPatient()} />
                            <button onClick={searchPatient}
                                style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                                Search
                            </button>
                        </div>
                        {searchResults.map(p => (
                            <div key={p.id} onClick={() => { setSelectedPatient(p); setSearchResults([]); setPatientSearch(''); }}
                                style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', marginBottom: 4, fontSize: 13 }}>
                                <strong>{p.mrn}</strong> — {p.full_name} | {p.gender} | Age: {p.age_years}
                            </div>
                        ))}
                        {selectedPatient && (
                            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, color: '#166534', display: 'flex', justifyContent: 'space-between' }}>
                                <span>✅ {selectedPatient.full_name} ({selectedPatient.mrn})</span>
                                <button onClick={() => setSelectedPatient(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Referred From</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.source} onChange={e => set('source', e.target.value)}>
                                {SOURCES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Sessions Prescribed</label>
                            <input type="number" min="1" max="100" style={inputStyle} value={form.sessions_prescribed}
                                onChange={e => set('sessions_prescribed', parseInt(e.target.value) || 10)} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Treatment Goal</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.treatment_goals} onChange={e => set('treatment_goals', e.target.value)}>
                                <option value="">— Select Goal —</option>
                                {GOALS.map(g => <option key={g}>{g}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Diagnosis / Condition *</label>
                            <input style={inputStyle} value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)}
                                placeholder="e.g. Lower Back Pain, Post-op Knee Replacement" />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                            <input style={inputStyle} value={form.notes} onChange={e => set('notes', e.target.value)}
                                placeholder="Any special instructions..." />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleCreate} disabled={saving}
                            style={{ padding: '9px 24px', background: saving ? '#9ca3af' : '#0369a1', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Creating...' : 'Create Referral'}
                        </button>
                        <button onClick={() => setShowForm(false)}
                            style={{ padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Worklist */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
            ) : referrals.length === 0 ? (
                <div style={{ background: 'white', borderRadius: 10, padding: 40, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🦽</div>
                    <div style={{ fontWeight: 600 }}>No active physio referrals</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Click "New Referral" to add one</div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {referrals.map(r => {
                        const pct = r.sessions_prescribed ? Math.min(100, Math.round((r.sessions_done / r.sessions_prescribed) * 100)) : 0;
                        return (
                            <div key={r.id} onClick={() => navigate(`/physio/${r.id}`)}
                                style={{ background: 'white', borderRadius: 10, padding: '14px 18px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, transition: 'box-shadow 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                                <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🦽</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{r.full_name}</div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>{r.mrn} · {r.gender} · Age {r.age_years}</div>
                                    <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{r.diagnosis}</div>
                                </div>
                                <div style={{ textAlign: 'center', minWidth: 80 }}>
                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Sessions</div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: progressColor(r.sessions_done, r.sessions_prescribed) }}>
                                        {r.sessions_done} / {r.sessions_prescribed}
                                    </div>
                                    <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginTop: 4 }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: progressColor(r.sessions_done, r.sessions_prescribed), borderRadius: 2, transition: 'width 0.3s' }} />
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', minWidth: 80 }}>
                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Referred From</div>
                                    <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{r.source}</span>
                                </div>
                                <div style={{ textAlign: 'center', minWidth: 90 }}>
                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Goal</div>
                                    <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{r.treatment_goals || '—'}</div>
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: 18 }}>›</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
